import { Draft } from "immer";
import { isBlank } from "utils/string";
import { NOTIFICATION_LIMITS } from "@dashboard-v3/shared";

import {
  DoneNotification,
  EmailExpiration,
  Reminder,
  SenderNotification,
} from "@dashboard-v3/api";
import { State } from "../../types";
import { getSendDelay } from "pages/Rules/Form/context/ruleSelectors";

export default function validate(draft: Draft<State>) {
  const {
    expiration,
    senderNotification,
    doneNotification,
    reminderSenderNotification,
    reminder,
  } = getSendDelay(draft.rule);

  const errors = [];

  const expirationValidation = validateExpiration(expiration);
  const reminderPeriodValidation = validateReminderPeriod(reminder, expiration);
  const senderValidation = validateSettings("SENDER", senderNotification);
  const doneValidation = validateSettings("COMPLETION", doneNotification);

  const reminderValidation = validateSettings(
    "REMINDER",
    reminderSenderNotification
  );

  if (!expirationValidation.isValid) {
    errors.push(expirationValidation.error);
  }

  if (!senderValidation.isValid) {
    errors.push(...senderValidation.errors);
  }

  if (!doneValidation.isValid) {
    errors.push(...doneValidation.errors);
  }

  if (!reminderValidation.isValid) {
    errors.push(...reminderValidation.errors);
  }

  if (!reminderPeriodValidation.isValid) {
    errors.push(reminderPeriodValidation.error);
  }

  draft.steps.sendDelay.error = errors;
  draft.steps.sendDelay.valid = errors.length === 0;
}

function getMaxLength(key: string): number | null {
  if (key.endsWith("Label")) return NOTIFICATION_LIMITS.CTA_LABEL;
  if (key === "title") return NOTIFICATION_LIMITS.TITLE;
  if (key.toLowerCase().includes("bodytext"))
    return NOTIFICATION_LIMITS.BODY_TEXT;
  return null;
}

function validateSettings(
  type: "COMPLETION" | "SENDER" | "REMINDER",
  notification: SenderNotification | DoneNotification
) {
  const errors: string[] = [];

  for (const key in notification) {
    const value = notification[key];

    if (typeof value !== "string") continue;

    if (isBlank(value)) {
      if (!key.endsWith("Label")) {
        errors.push(`${type}_${key.toUpperCase()}_REQUIRED`);
        continue;
      }

      const linkKey = key.replace("Label", "");
      const isLinkEnabled = notification[linkKey] === true;

      if (isLinkEnabled) {
        errors.push(`${type}_${linkKey.toUpperCase()}_FIELD_ERROR`);
      }
      continue;
    }

    const maxLength = getMaxLength(key);
    if (maxLength && value.length > maxLength) {
      errors.push(`${type}_${key.toUpperCase()}_MAX_LENGTH`);
    }
  }

  return {
    isValid: errors.length === 0,
    errors,
  };
}

function validateExpiration(expiration: EmailExpiration) {
  if (!expiration) {
    return { isValid: false, error: "EXPIRATION_INVALID" };
  }

  const { type } = expiration;
  const value = Number(expiration.value);

  switch (type) {
    case "DAYS":
      if (value < 1) return { isValid: false, error: "EXCEEDS_UNIT_MIN" };
      if (value > 10) return { isValid: false, error: "EXCEEDS_VALUE_MAX" };
      break;
    case "HOURS":
      if (value < 1) return { isValid: false, error: "EXCEEDS_UNIT_MIN" };
      if (value > 24) return { isValid: false, error: "EXCEEDS_UNIT_MAX" };
      break;
    case "MINUTES":
      if (value < 2) return { isValid: false, error: "EXCEEDS_VALUE_MIN" };
      if (value > 60) return { isValid: false, error: "EXCEEDS_UNIT_MAX" };
      break;
  }

  return { isValid: true, error: "" };
}

export function validateReminderPeriod(
  reminder: Reminder,
  expiration: EmailExpiration
) {
  if (reminder === null || typeof reminder === "undefined") {
    return { isValid: true };
  }

  // Convert both to hours for easier comparison
  const expirationHours =
    expiration.type === "HOURS" ? expiration.value : expiration.value * 24;

  const reminderHours =
    reminder.type === "HOURS" ? reminder.value : reminder.value * 24;

  // Reminder cannot be greater than expiration
  if (reminderHours > expirationHours) {
    return { isValid: false, error: "REMINDER_INTERVAL_EXCEEDED" };
  }

  let minIntervalHours: number;

  if (expirationHours < 24) {
    minIntervalHours = 2;
  } else if (expirationHours >= 24 && expirationHours <= 72) {
    minIntervalHours = 6;
  } else if (expirationHours >= 72 && expirationHours <= 168) {
    minIntervalHours = 12;
  } else {
    minIntervalHours = 48;
  }

  if (reminderHours < minIntervalHours) {
    return { isValid: false, error: "REMINDER_INTERVAL_MIN_ERROR" };
  }

  return { isValid: true };
}
